import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createBrowserRouter, RouterProvider, Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ThemeProvider } from "styled-components";

import { dark } from "./styles/theme";
import { GlobalStyle } from "./styles/GlobalStyles";
import AppBootScreen from "./components/AppBootScreen.mjs";
import Layout from "./components/Layout";
import Analytics from "./pages/Analytics";
import { CertProvider } from "./contexts/CertContext";
import { AppDataProvider } from "./contexts/AppDataContext";
import api, { setAuthTokenGetter } from "./services/api";
import { FEATURES } from "./config/features";
import { useAuth0 } from "@auth0/auth0-react";
import ErrorBoundary from "./components/ErrorBoundary";
import { useSeo } from "./utils/seo";
import { getAttribution } from "./utils/funnelTracking";
import { certPath, normalizeCertCode } from "./utils/certCatalog";
import { LEGACY_PROVIDER_SLUGS } from "./utils/publicProviders.mjs";
// EnvBadge intentionally unused pre-launch — we already know which
// environment we're in and the orange badge was just visual noise.
// import EnvBadge from "./components/EnvBadge";

// Lazy-load most routes. Analytics ships with the app so its prepared
// dashboard data can render immediately when the learner opens it.

// Auto-retry + reload on ChunkLoadError. After a deploy the old chunk hashes
// disappear; a single page reload picks up the new main.js with correct refs.
const lazyRetry = (importFn) =>
  React.lazy(() =>
    importFn().catch((err) => {
      if (err.name === "ChunkLoadError" && !window.__chunkReloadAttempted) {
        window.__chunkReloadAttempted = true;
        window.location.reload();
        return new Promise(() => {}); // hang until reload completes
      }
      throw err;
    })
  );

const LandingPage = lazyRetry(() => import("./pages/LandingPage"));
const LandingVariantsPage = lazyRetry(() => import("./pages/LandingVariantsPage"));
const CertLandingPage = lazyRetry(() => import("./pages/CertLandingPage"));
const CertificationsPage = lazyRetry(() => import("./pages/CertificationsPage"));
const CertificationLabsPage = lazyRetry(() => import("./pages/PublicSeoPages").then((m) => ({ default: m.CertificationLabsPage })));
const PublicLabPage = lazyRetry(() => import("./pages/PublicSeoPages").then((m) => ({ default: m.PublicLabPage })));
const GuidesIndexPage = lazyRetry(() => import("./pages/PublicSeoPages").then((m) => ({ default: m.GuidesIndexPage })));
const GuidePage = lazyRetry(() => import("./pages/PublicSeoPages").then((m) => ({ default: m.GuidePage })));
const ProviderLandingPage = lazyRetry(() => import("./pages/ProviderLandingPage"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const ExamSetup = lazyRetry(() => import("./pages/ExamSetup"));
const Exam = lazyRetry(() => import("./pages/Exam"));
const Study = lazyRetry(() => import("./pages/Study"));
const RoadmapV2Home = lazyRetry(() => import("./pages/RoadmapV2Home"));

const Profile = lazyRetry(() => import("./pages/Profile"));
const Onboarding = lazyRetry(() => import("./pages/Onboarding"));
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const AdminDashboard = lazyRetry(() => import("./pages/AdminDashboard"));
const DevCastle = lazyRetry(() => import("./pages/DevCastle"));
const Labs = lazyRetry(() => import("./pages/Labs"));
const LabWorkspace = lazyRetry(() => import("./pages/LabWorkspace"));
const StartFreeExam = lazyRetry(() => import("./pages/StartFreeExam"));
const FreeExamRedirect = lazyRetry(() => import("./pages/FreeExamRedirect"));

// Match the document's first frame while auth or a route chunk is pending.
const RouteFallback = AppBootScreen;

// Inline 404 — dropping the user on Dashboard used to silently swallow
// broken links (stale cert URLs, retired public pages, etc.). A real page is
// better UX and means Google sees a deliberate "not found" response
// instead of a redirect chain.
const NotFound = () => {
  // Real noindex meta, not just robots.txt: unknown URLs are served the
  // SPA shell with HTTP 200, so without this tag Google can index junk
  // URLs as soft duplicates of the homepage.
  useSeo({
    title: "Page not found",
    description: "This page doesn't exist on ExamNova. Head back to the dashboard or browse the certification catalog.",
    noIndex: true,
  });
  React.useEffect(() => {
    // The SPA fallback shell is the prerendered homepage, which carries
    // canonical=https://examnova.pro/ — actively wrong on a 404. Drop it;
    // any page navigated to next that owns a canonical re-creates it.
    const stale = document.head.querySelector('link[rel="canonical"]');
    if (stale) stale.remove();
  }, []);
  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0f1a",
      color: "#e2e8f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 20px",
      fontFamily: "inherit",
    }}>
      <div style={{ maxWidth: 520, textAlign: "center" }}>
        <div style={{
          fontSize: "0.7rem",
          fontWeight: 900,
          letterSpacing: "2px",
          color: "#3b82f6",
          marginBottom: 16,
        }}>
          ERROR 404
        </div>
        <h1 style={{
          fontSize: "2rem",
          fontWeight: 900,
          color: "#f8fafc",
          margin: "0 0 12px",
          letterSpacing: "-0.02em",
        }}>
          Page not found
        </h1>
        <p style={{
          fontSize: "0.95rem",
          color: "#64748b",
          lineHeight: 1.6,
          margin: "0 0 32px",
        }}>
          The link you followed is broken, or the page has been moved.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link
            to="/"
            style={{
              padding: "12px 24px",
              background: "#3b82f6",
              color: "#fff",
              borderRadius: 8,
              fontWeight: 800,
              fontSize: "0.85rem",
              textDecoration: "none",
              letterSpacing: "0.5px",
            }}
          >
            Go Home
          </Link>
          <Link
            to="/cert/az-104"
            style={{
              padding: "12px 24px",
              background: "transparent",
              color: "#94a3b8",
              border: "1px solid #1e293b",
              borderRadius: 8,
              fontWeight: 800,
              fontSize: "0.85rem",
              textDecoration: "none",
              letterSpacing: "0.5px",
            }}
          >
            Browse Certifications
          </Link>
        </div>
      </div>
    </div>
  );
};

const lazyEl = (Comp) => (
  <React.Suspense fallback={<RouteFallback />}>
    <Comp />
  </React.Suspense>
);

const roadmapV2El = (Comp) => (FEATURES.roadmapV2 ? lazyEl(Comp) : <Navigate to="/" replace />);
const studyEl = (Comp) => (FEATURES.study ? lazyEl(Comp) : <Navigate to="/" replace />);
const labsEl = (Comp) => (FEATURES.labs ? lazyEl(Comp) : <Navigate to="/" replace />);
// Internal dev toy — the route simply doesn't exist unless the flag is on.
const devCastleEl = (Comp) => (FEATURES.devCastle ? lazyEl(Comp) : <Navigate to="/" replace />);

const LegacyRoadmapNodeRedirect = () => {
  const { certCode = "AZ-104", nodeId = "" } = useParams();
  const cert = String(certCode).toLowerCase();
  const subdomain = encodeURIComponent(nodeId || "current");
  return <Navigate to={`/roadmap-v2/${cert}/subdomain/${subdomain}`} replace />;
};

// /hub, /practice and /domain were three tiers below a certification page
// that each repeated it and linked back to it. They now resolve to the one
// page that holds the whole certification.
const LegacyCertTierRedirect = () => {
  const { certCode = "SAA-C03" } = useParams();
  return <Navigate to={certPath(normalizeCertCode(certCode) || "SAA-C03")} replace />;
};

const LegacyCertificationRedirect = () => {
  const { code = "" } = useParams();
  const location = useLocation();
  return <Navigate to={`${certPath(code)}${location.search}${location.hash}`} replace />;
};

export const AuthGate = ({
  children,
  onboardingNeeded,
  onOnboardingComplete,
  profileReady,
  profileError = null,
  onRetryProfile,
}) => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const publicSignupStarted = React.useRef(false);

  React.useEffect(() => {
    // The prerendered home CTA remains a real link before React is ready.
    // Only that explicit public-home intent launches platform signup.
    if (isLoading || isAuthenticated || publicSignupStarted.current) return;
    if (location.pathname !== "/" || new URLSearchParams(location.search).get("start") !== "free") return;
    publicSignupStarted.current = true;
    loginWithRedirect({
      appState: { returnTo: "/" },
      authorizationParams: { screen_hint: "signup" },
    }).catch(() => {});
  }, [isAuthenticated, isLoading, location.pathname, location.search, loginWithRedirect]);

  if (!isLoading && isAuthenticated && profileError) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, background: "#0a0f1a", color: "#94a3b8", textAlign: "center" }}>
        <div style={{ maxWidth: 440 }}>
          <h1 style={{ margin: "0 0 10px", color: "#f8fafc", fontSize: "1.5rem" }}>We couldn't finish setting up your account</h1>
          <p role="alert" style={{ margin: "0 0 22px", lineHeight: 1.6 }}>Your account was not changed. Check your connection and try again.</p>
          <button
            type="button"
            onClick={onRetryProfile}
            style={{ border: 0, borderRadius: 8, padding: "11px 18px", background: "#6366f1", color: "#fff", cursor: "pointer", fontWeight: 800 }}
          >
            Retry account setup
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || (isAuthenticated && !profileReady)) {
    return <AppBootScreen />;
  }

  if (!isAuthenticated) {
    if (location.pathname.replace(/\/+$/, "") === "/support") {
      return <Navigate to="/" replace />;
    }
    return (
      <React.Suspense fallback={<RouteFallback />}>
        <LandingPage onLoginClick={async ({ intent } = {}) => {
          try {
            const startsFree = intent === "start_free";
            const options = {
              appState: {
                returnTo: startsFree
                  ? "/"
                  : `${location.pathname}${location.search}${location.hash}`,
              },
            };
            if (startsFree) {
              options.authorizationParams = { screen_hint: "signup" };
            }
            await loginWithRedirect(options);
          }
          catch (e) {
            if (process.env.NODE_ENV !== "production") {
              console.error("Auth0 Login Error:", e);
            }
          }
        }} />
      </React.Suspense>
    );
  }

  if (onboardingNeeded) {
    return (
      <React.Suspense fallback={<RouteFallback />}>
        <Onboarding onComplete={onOnboardingComplete} />
      </React.Suspense>
    );
  }

  return children;
};



export default function App() {
  const { isAuthenticated, logout, user, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // App is dark-mode only. Light theme exists in theme.js but the full component
  // library uses hardcoded dark values — a proper light-mode pass is post-launch.
  const themeName = "dark";
  const [onboardingNeeded, setOnboardingNeeded] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileRetry, setProfileRetry] = useState(0);
  const [accountTargetCert, setAccountTargetCert] = useState("");

  const retryProfileSetup = useCallback(() => {
    setProfileRetry(value => value + 1);
  }, []);

  // Inject the token getter SYNCHRONOUSLY during render — not in a useEffect.
  // AppDataContext is a child of App, and in React child effects run BEFORE
  // parent effects, so a useEffect here would fire too late and the very first
  // API call would go out without an Authorization header. Assigning a module
  // variable during render is safe (no state update, idempotent).
  setAuthTokenGetter(() => getAccessTokenSilently({
    authorizationParams: {
      audience: (process.env.REACT_APP_AUTH0_AUDIENCE || "").trim(),
    }
  }));

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setOnboardingNeeded(false);
      setProfileReady(false);
      setProfileError(null);
      setAccountTargetCert("");
      return undefined;
    }

    let cancelled = false;
    setProfileReady(false);
    setProfileError(null);

    localStorage.setItem("userId", user.sub);
    const refCode = localStorage.getItem('referredBy');
    api.post("/api/users", {
      userId: user.sub,
      email: user.email,
      referredBy: refCode,
      attribution: getAttribution(),
    })
      .then(res => {
        if (cancelled) return;
        setOnboardingNeeded(Boolean(res.data && !res.data.onboardingCompleted));
        setAccountTargetCert(normalizeCertCode(res.data?.targetCertCode || ""));
        if (refCode) localStorage.removeItem('referredBy');
        setProfileReady(true);
      })
      .catch(err => {
        if (cancelled) return;
        if (process.env.NODE_ENV !== "production") {
          console.error("User setup error:", err);
        }
        setProfileError(err);
      })

    return () => { cancelled = true; };
  }, [isAuthenticated, user, profileRetry]);

  const handleLogout = () => {
    localStorage.removeItem("userId");
    localStorage.removeItem("displayName");
    localStorage.removeItem("streakCount");
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleLandingVariantsLogin = useCallback(({ intent = "start_free", certification = "" } = {}) => {
    const startsFree = intent === "start_free";
    const returnTo = startsFree && certification
      ? `/start-free-exam?cert=${encodeURIComponent(certification)}`
      : "/";
    const options = { appState: { returnTo } };
    if (startsFree) options.authorizationParams = { screen_hint: "signup" };
    loginWithRedirect(options).catch(() => {});
  }, [loginWithRedirect]);




  const currentTheme = dark;

  const router = useMemo(() => createBrowserRouter([
    {
      // The concept lab is intentionally local-only. It lets the team compare
      // signup-first landing directions without adding a production detour.
      path: "/landing-variants",
      element: window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
        ? (
          <ThemeWrapper theme={currentTheme}>
            <React.Suspense fallback={<RouteFallback />}>
              <LandingVariantsPage onLoginClick={handleLandingVariantsLogin} />
            </React.Suspense>
          </ThemeWrapper>
        )
        : <Navigate to="/" replace />,
    },
    {
      path: "/roadmap-v2",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={onboardingNeeded} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          {roadmapV2El(RoadmapV2Home)}
        </AuthGate>
      ),
    },
    {
      path: "/roadmap-v2/:certCode",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={onboardingNeeded} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          {roadmapV2El(RoadmapV2Home)}
        </AuthGate>
      ),
    },
    {
      path: "/roadmap-v2/:certCode/subdomain/:subdomainId",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={onboardingNeeded} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          {roadmapV2El(RoadmapV2Home)}
        </AuthGate>
      ),
    },
    {
      path: "/roadmap-v2/:certCode/unit/:unitId",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={onboardingNeeded} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          <Navigate to="/study" replace />
        </AuthGate>
      ),
    },
    {
      path: "/roadmap-v2-proof/:certCode?",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={onboardingNeeded} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          <Navigate to="/study" replace />
        </AuthGate>
      ),
    },
    {
      path: "/labs/:labId",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={onboardingNeeded} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          {labsEl(LabWorkspace)}
        </AuthGate>
      ),
    },
    {
      path: "/",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={onboardingNeeded} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          <Layout onLogout={handleLogout} />
        </AuthGate>
      ),
      children: [
        { index: true, element: lazyEl(Dashboard) },
        { path: "first-session", element: <Navigate to="/" replace /> },
        { path: "setup", element: lazyEl(ExamSetup) },
        { path: "exam/:sessionId", element: lazyEl(Exam) },
        { path: "start-free-exam", element: lazyEl(StartFreeExam) },
        { path: "study", element: studyEl(Study) },
        { path: "cert/:certCode/node/:nodeId", element: roadmapV2El(LegacyRoadmapNodeRedirect) },
        { path: "labs", element: labsEl(Labs) },

        { path: "analytics", element: <Analytics /> },
        { path: "profile", element: lazyEl(Profile) },
        { path: "support", element: <Navigate to="/" replace /> },
        { path: "admin", element: lazyEl(AdminDashboard) },
      ]
    },
    {
      path: "/onboarding",
      element: (
        <AuthGate profileReady={profileReady} profileError={profileError} onRetryProfile={retryProfileSetup} onboardingNeeded={false} onOnboardingComplete={() => setOnboardingNeeded(false)}>
          <React.Suspense fallback={<RouteFallback />}>
            <Onboarding onComplete={() => setOnboardingNeeded(false)} />
          </React.Suspense>
        </AuthGate>
      ),
    },
    {
      // Dev Castle (Nova Keep) — internal local-development dashboard. The
      // feature gate removes this route from shared staging and production.
      path: "/dev/castle",
      element: <ThemeWrapper theme={currentTheme}>{devCastleEl(DevCastle)}</ThemeWrapper>,
    },
    {
      path: "/hub/:certCode/:topicSlug",
      element: <LegacyCertTierRedirect />
    },
    {
      path: "/practice/:certCode/:topicSlug",
      element: <LegacyCertTierRedirect />
    },
    {
      // Preserve old public links without matching authenticated node routes.
      path: "/cert/:code",
      element: <LegacyCertificationRedirect />,
    },
    {
      // Canonical public landing page for a specific certification.
      path: "/certifications/:code",
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <CertLandingPage onLoginClick={() => loginWithRedirect()} />
          </React.Suspense>
        </ThemeWrapper>
      ),
    },
    {
      path: "/domain/:certCode/:domainSlug",
      element: <LegacyCertTierRedirect />,
    },
    {
      path: "/certification-labs",
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <CertificationLabsPage />
          </React.Suspense>
        </ThemeWrapper>
      ),
    },
    {
      path: "/lab/:labId",
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <PublicLabPage />
          </React.Suspense>
        </ThemeWrapper>
      ),
    },
    {
      path: "/guides",
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <GuidesIndexPage />
          </React.Suspense>
        </ThemeWrapper>
      ),
    },
    {
      path: "/guides/:slug",
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <GuidePage />
          </React.Suspense>
        </ThemeWrapper>
      ),
    },
    {
      path: "/certifications",
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <CertificationsPage onLoginClick={() => loginWithRedirect()} />
          </React.Suspense>
        </ThemeWrapper>
      ),
    },
    {
      path: "/providers/:providerSlug",
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <ProviderLandingPage onLoginClick={() => loginWithRedirect()} />
          </React.Suspense>
        </ThemeWrapper>
      ),
    },
    ...LEGACY_PROVIDER_SLUGS.map((providerSlug) => ({
      path: `/${providerSlug}`,
      element: (
        <ThemeWrapper theme={currentTheme}>
          <React.Suspense fallback={<RouteFallback />}>
            <ProviderLandingPage
              providerSlugOverride={providerSlug}
              onLoginClick={() => loginWithRedirect()}
            />
          </React.Suspense>
        </ThemeWrapper>
      ),
    })),
    {
      // Legacy public free-exam URLs now enter through Auth0 and then launch
      // the canonical authenticated Free Practice Exam.
      path: "/free-exam/:code",
      element: <ThemeWrapper theme={currentTheme}>{lazyEl(FreeExamRedirect)}</ThemeWrapper>,
    },
    {
      path: "/free-exam/:code/:sessionId",
      element: <ThemeWrapper theme={currentTheme}>{lazyEl(FreeExamRedirect)}</ThemeWrapper>,
    },
    {
      path: "/privacy",
      element: lazyEl(PrivacyPolicy)
    },
    {
      path: "/terms",
      element: lazyEl(Terms)
    },
    {
      path: "*",
      element: <ThemeWrapper theme={currentTheme}><NotFound /></ThemeWrapper>
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [handleLandingVariantsLogin, onboardingNeeded, profileError, profileReady, retryProfileSetup, themeName]);


  return (
    <ThemeProvider theme={currentTheme}>
      <GlobalStyle />
      <ErrorBoundary>
        <CertProvider preferredCert={accountTargetCert}>
          <AppDataProvider>
            <RouterProvider router={router} />
          </AppDataProvider>
        </CertProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

const ThemeWrapper = ({ children, theme }) => (
  <ThemeProvider theme={theme}>
    <GlobalStyle />
    {children}
  </ThemeProvider>
);
